import React, { useState, useMemo } from 'react';
import { Search, Calendar, User, Trash2 } from 'lucide-react';
import { ResponseWithAnswersDto } from '../../services/feedbackService';

interface ResponsesListProps {
  responses: ResponseWithAnswersDto[];
  onResponseClick: (response: ResponseWithAnswersDto) => void;
  onDeleteResponse?: (responseId: string) => void;
  isDeleting?: boolean;
}

const ResponsesList: React.FC<ResponsesListProps> = ({ responses, onResponseClick, onDeleteResponse, isDeleting }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const filteredResponses = useMemo(() => {
    if (!searchTerm) return responses;
    const lowerSearch = searchTerm.toLowerCase();
    return responses.filter(
      (response) =>
        response.respondent?.fullName?.toLowerCase().includes(lowerSearch) ||
        response.respondent?.email?.toLowerCase().includes(lowerSearch)
    );
  }, [responses, searchTerm]);

  const handleDeleteClick = (e: React.MouseEvent, responseId: string) => {
    e.stopPropagation();
    setConfirmDeleteId(responseId);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmDeleteId && onDeleteResponse) {
      onDeleteResponse(confirmDeleteId);
      setConfirmDeleteId(null);
    }
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  if (responses.length === 0) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 dark:text-gray-400">No responses yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Respondent
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Submitted
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Answers
              </th>
              {onDeleteResponse && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredResponses.map((response) => (
              <tr
                key={response.id}
                onClick={() => onResponseClick(response)}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {response.respondent?.fullName || response.respondent?.email || 'Anonymous'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {response.respondent?.email || '-'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Calendar className="h-4 w-4 mr-2" />
                    {response.submittedAt
                      ? new Date(response.submittedAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'In Progress'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                  {response.answers?.length || 0} answers
                </td>
                {onDeleteResponse && (
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {confirmDeleteId === response.id ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-sm text-red-600 dark:text-red-400">Delete?</span>
                        <button
                          onClick={handleConfirmDelete}
                          disabled={isDeleting}
                          className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
                        >
                          {isDeleting ? 'Deleting...' : 'Yes'}
                        </button>
                        <button
                          onClick={handleCancelDelete}
                          disabled={isDeleting}
                          className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded disabled:opacity-50"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => handleDeleteClick(e, response.id)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Delete response"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {filteredResponses.length === 0 && searchTerm && (
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-400">No responses matching "{searchTerm}"</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponsesList;
